import { useState } from 'react';

export interface WeightStep {
  condition: string;
  weight: number;
}

export interface ScoreComponent {
  key: string;
  label: string;
  maxWeight: number;
  hex: string;
  textHex: string;
  sources: string[];
  weightSteps?: WeightStep[];
}

interface ScoreComponentsVisualProps {
  components: ScoreComponent[];
  note?: string;
  showMaxSymbol?: boolean;
}

function WeightTooltip({ comp, visible }: { comp: ScoreComponent; visible: boolean }) {
  if (!visible || !comp.weightSteps) return null;

  return (
    <div className="absolute bottom-full right-0 mb-2 z-[200] pointer-events-none" style={{ minWidth: 210 }}>
      <div className="bg-gray-900 text-white rounded-md shadow-xl px-3 py-2.5">
        <p
          className="text-[10px] font-semibold uppercase tracking-wide mb-2"
          style={{ color: comp.hex === '#0a104f' ? '#93C5FD' : comp.hex }}
        >
          {comp.label} — weight scale
        </p>
        {comp.weightSteps.map((step, i) => (
          <div key={i} className="flex justify-between gap-4 py-0.5">
            <span className="text-[10px] text-gray-300">{step.condition}</span>
            <span
              className="text-[10px] font-bold tabular-nums"
              style={{ color: step.weight > 0 ? '#fff' : '#6B7280' }}
            >
              {step.weight.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
      {/* Arrow */}
      <div className="absolute top-full right-5 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-gray-900" />
    </div>
  );
}

export function ScoreComponentsVisual({
  components,
  note,
  showMaxSymbol = true,
}: ScoreComponentsVisualProps) {
  const totalMax = components.reduce((sum, c) => sum + c.maxWeight, 0);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  return (
    <div className="my-4">
      {/* Segmented bar */}
      <div className="flex h-7 w-full rounded-md overflow-hidden border border-gray-200 shadow-inner">
        {components.map(comp => {
          const widthPct = (comp.maxWeight / totalMax) * 100;
          return (
            <div
              key={comp.key}
              className="h-full flex items-center justify-center"
              style={{ width: `${widthPct}%`, backgroundColor: comp.hex }}
            >
              {widthPct > 11 && (
                <span className="text-white text-[10px] font-semibold truncate px-1 select-none pointer-events-none uppercase">
                  {comp.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Component rows */}
      <div className="mt-2 space-y-0.5">
        {components.map(comp => {
          const widthPct = (comp.maxWeight / totalMax) * 100;
          const isHovered = hoveredKey === comp.key;

          return (
            <div
              key={comp.key}
              className="relative flex items-center gap-2 px-1 py-1 rounded"
              onMouseEnter={() => comp.weightSteps && setHoveredKey(comp.key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: comp.hex }} />
              <span className="flex-1 text-xs font-medium min-w-0" style={{ color: comp.textHex }}>
                {comp.label}
                {comp.sources.length > 0 && (
                  <span className="font-normal text-gray-400 ml-1.5 text-[10px]">
                    {comp.sources.join(', ')}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${widthPct}%`, backgroundColor: comp.hex }}
                  />
                </div>
                <span
                  className={`text-xs font-semibold tabular-nums w-10 text-right ${comp.weightSteps ? 'underline decoration-dashed cursor-help' : ''}`}
                  style={{ color: comp.textHex }}
                >
                  {showMaxSymbol ? '≤\u202f' : ''}
                  {comp.maxWeight.toFixed(2)}
                </span>
              </div>

              <WeightTooltip comp={comp} visible={isHovered} />
            </div>
          );
        })}
      </div>

      {note && <p className="text-[10px] text-gray-400 italic mt-2 px-1">{note}</p>}
    </div>
  );
}
